import React, { useState, useEffect, useRef, useMemo } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import API from '../../../utils/API';
import MesTable from '../../../components/Table/MesTable';
import tableCheckbox from '../../../components/Inputs/tableCheckbox';
import constants from '../../../utils/constants';
import commonMethods from '../../../utils/common-methods';
import useInfiniteScroll from '../../../components/common/useInfiniteScroll';
import ReceiversModal from '../../../components/SignagePlus/ReceiversModal';

const SIGNAGE_TEXT_PREFIX = "SIGNAGE_";

function SignageLogs(props) {
    const navigate = useNavigate();
    const { user } = props;
    const intl = useIntl();
    const permissionIds = user.permissions.map(permission => permission.id);

    if (!permissionIds.includes(constants.permissionIds.signagePlusEdit)) {
        navigate('/forbidden');
    }
    const [data, setData] = useState([]);
    const [dataCount, setDataCount] = useState(0);
    const [totalDataCount, setTotalDataCount] = useState(0);
    const [searchValue, setSearchValue] = useState('');
    const [dataLoaded, setDataLoaded] = useState(false);
    const [selectedIds, setSelectedIds] = useState({});
    const [sortByCol, setSortByCol] = useState('dateAndTime');
    const [sortByAsc, setSortByAsc] = useState(false);
    const [startIndexForTable, setStartIndexForTable] = useState(0);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [historyData, setHistoryData] = useState([]);
    const [modalData, setModalData] = useState([]);

    const tableRef = useRef();
    const tableWrapperRef = useRef();

    const loadTable = async (map, resetSelection = true) => {
        map.data = {};
        map.ids = [];
        map.sortedIds = [];
        map.filteredIds = [];
        if (resetSelection) {
            map.selectedIds = {};
        }

        try {
            const res = await API.getSignageHistories();
            res.data.forEach((signage) => {
                map.data[signage.id] = {
                    id: signage.id,
                    title: signage.title,
                    scheduled: commonMethods.formatScheduler(signage.scheduler),
                    groups: signage.groups?.map((group) => group.name).join(', '),
                    status: intl.formatMessage({ id: SIGNAGE_TEXT_PREFIX + signage.status }),
                    receivers: signage.receivers,
                    checked: false,
                };
            });
            map.filteredIds = Object.keys(map.data);
            map.ids = Object.keys(map.data);
            console.log('map.data', map.data)
            setDataCount(map.ids.length);
            setHistoryData(res.data);
            setTotalDataCount(map.ids.length);
            setDataLoaded(true);
        } catch (err) {
            setDataLoaded(true);
            if (err.response?.data?.error)
                return;
            setError(err.message);
        }
    };

    const dataMap = useMemo(() => {
        const map = { data: {}, ids: [], selectedIds: {}, filteredIds: [], sortedIds: [] };
        loadTable(map);
        return map;
    }, [intl]);

    // Hook for infinite scrolling
    const { generateDataSlice, sortMap, setSortBy } = useInfiniteScroll({
        dataMap,
        setData,
        setStartIndexForTable,
        startIndexForTable,
        pageSize: 100,
        sortByCol,
        setSortByCol,
        sortByAsc,
        setSortByAsc,
        tableWrapperRef,
        searchValue,
        tableRef,
        rowHeight: 48,
    });

    // Hook for sorting data when data changes or sort column is changed
    useEffect(() => {
        if (dataLoaded) {
            if (searchValue !== '') {
                tableRef.current.search(dataMap);
            } else {
                sortMap(dataMap);
                setData(generateDataSlice(dataMap));
            }
        }
    }, [sortByCol, sortByAsc, dataLoaded, dataMap, historyData]);

    const handleExportCsv = () => {
        const columnMapping = {
            'Signage': 'title',
            'Scheduled': 'scheduled',
            'Groups': 'groups',
            'Status': 'status'
        };

        commonMethods.exportToCsv({
            dataMap,
            selectedIds,
            columnMapping,
            filename: 'signage_logs.csv'
        });
    };

    function handleViewReceiversClick(receivers) {
        setModalData(receivers);
    }

    const tableColumnArray = [
        {
            Header: tableCheckbox,
            id: 'checkbox',
            accessor: 'checked',
            Cell: tableCheckbox,
            isVisible: true,
            width: 63,
            maxWidth: 63,
        },
        {
            Header: 'SIGNAGE',
            id: 'title',
            accessor: 'title',
            isVisible: true,
        },
        {
            Header: 'SCHEDULED',
            id: 'scheduled',
            accessor: 'scheduled',
            isVisible: true,
        },
        {
            Header: 'GROUPS',
            id: 'groups',
            accessor: 'groups',
            isVisible: true,
        },
        {
            Header: 'STATUS',
            id: 'status',
            accessor: 'status',
            isVisible: true,
        },
        {
            Header: 'RECEIVERS',
            id: 'mesViewReceivers',
            Cell: ({ row }) => (
                <div
                    type="button"
                    className="btn p-0 viewUsersBtn"
                    data-bs-toggle="modal"
                    data-bs-target="#viewReceiversModal"
                    onClick={() => {
                        console.log('row.original.receivers', row.original.receivers, row.original)
                        handleViewReceiversClick(row.original.receivers)
                    }}
                >
                    <FormattedMessage id="VIEW_RECEIVERS" />
                </div>
            ),
            isVisible: true,
        }
    ];

    const buttons = {
        footerButtons: (
            <>
                <button
                    id="exportCsvBtn"
                    type="button"
                    className="btn"
                    onClick={handleExportCsv}
                    disabled={Object.keys(selectedIds).length === 0}
                >
                    <span><FormattedMessage id="EXPORT_AS_CSV" defaultMessage="Export As CSV" /></span>
                </button>
            </>
        ),
    };

    const tableProps = {
        // The tableKey is for task#20542.
        // Each table requires a unique key to prevent preferences from being overwritten.
        tableKey: "sbds_admin_tasks_logs_table",
        pageType: constants.pageType.MessageLogs,
        modalId: "SignageLogs",
        columns: tableColumnArray,
        data, setData,
        dataMap,
        generateDataSlice,
        sortMap,
        dataCount, setDataCount,
        totalDataCount,
        setSortBy,
        sortByCol,
        sortByAsc,
        success, setSuccess,
        error, setError,
        startIndex: startIndexForTable,
        selectedIds, setSelectedIds,
        searchValue,
        setSearchValue,
        tableRef,
        tableWrapperRef,
        dataLoaded,
        buttons
    };

    return (
        <>
            <ReceiversModal 
              receivers={modalData} 
              onClose={() => setModalData([])} 
            />
            <MesTable {...tableProps} />
        </>
    );
}

const mapStateToProps = (state) => ({
    user: state.user
});

export default connect(mapStateToProps)(SignageLogs);
